import React from 'react';
import { motion } from 'framer-motion';
import FloatingBackground from '../../Common/FloatingBackground/FloatingBackground';
import './Trust.css';

const Trust: React.FC = () => {

  const trustReasons = [
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9ZM19 9H14V4H19V9Z" fill="currentColor"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Clinical Trial Support Services That Deliver",
      description: "We deliver tangible efficiencies in trial schedules, participant involvement, and **clinical trial data accuracy**."
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Security First and Compliance",
      description: "All solutions comply with international regulations such as **GDPR**, **SOC 2**, **ISO 27001**, and **HIPAA**."
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 2A10 10 0 0 1 12 22A10 10 0 0 1 12 2Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: "Flexible, Scalable Delivery",
      description: "Whether we work at a single site or globally, our **systems and teams** scale effortlessly."
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "CDT (Clinical Digital Transformation)",
      description: "We help you adopt modern technology and automation to simplify **data capture**, remote patient monitoring, and **electronic trial workflows**."
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 12H22" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2C14.5013 2 16.8359 2.79018 18.6569 4.61116C20.4778 6.43214 21.268 8.76674 21.268 11.268C21.268 13.7693 20.4778 16.1039 18.6569 17.9249C16.8359 19.7458 14.5013 20.536 12 20.536C9.49872 20.536 7.16412 19.7458 5.34314 17.9249C3.52216 16.1039 2.73198 13.7693 2.73198 11.268C2.73198 8.76674 3.52216 6.43214 5.34314 4.61116C7.16412 2.79018 9.49872 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Open Cooperation",
      description: "Frequent **communication**, **real-time reporting**, and dedicated project teams ensure responsiveness and clarity."
    }
  ];

  return (
    <motion.section 
      className="trust modern-trust"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <FloatingBackground />
      <div className="container">
        <div className="section-header">
          <h2 className="section-title modern-title">
            The Reasons Why Sponsors and CROs <span className="gradient-text">Trust Cinquity</span>
          </h2>
          <p className="section-subtitle">
            Experience the reliability, security, and innovation that make us the preferred partner for clinical trial excellence
          </p>
        </div>
        
        <div className="trust-grid">
          {trustReasons.map((reason, index) => (
            <div key={index} className="trust-card floating-card">
              <div className="trust-icon">
                <div className="icon-bg">
                  {reason.icon}
                </div>
              </div>
              <h3 className="trust-title">{reason.title}</h3>
              <p className="trust-description" dangerouslySetInnerHTML={{ __html: reason.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default Trust;
